import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Radio,
  Checkbox,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  Divider,
  notification,
  Segmented,
} from 'antd';
import {
  DownloadOutlined,
  ExportOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { FACILITY_EXPORT_FORMATS } from './generateFacilityExport';
import {
  defaultFacilityExportFieldKeys,
  downloadFacilityExportClient,
  FORMAT_LABELS,
  notifyFacilityDownloadComplete,
  notifyFacilityExportFailed,
  notifyFacilityExportQueued,
  parseFacilityExportFieldsResponse,
  queueFacilityExportJob,
  sanitizeFacilityExportFieldKeys,
  shouldUseClientInstantExport,
  isServerQueuedExportFormat,
  trySyncFacilityExport,
} from './facilityExportHelpers';
import './FacilitiesExportModal.scss';

const { Text, Paragraph } = Typography;

const PRESET_LABELS = {
  basic: 'Basic',
  contact: 'Contact',
  full: 'Full',
};

const FORMAT_ICONS = {
  csv: <FileTextOutlined />,
  xlsx: <FileExcelOutlined />,
  pdf: <FilePdfOutlined />,
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} [props.search]
 * @param {number|null} [props.estimatedRows]
 * @param {() => void} [props.onViewExports]
 * @param {number} [props.modalZIndex]
 */
const FacilitiesExportModal = ({
  open,
  onClose,
  search = '',
  estimatedRows = null,
  onViewExports,
  modalZIndex,
}) => {
  const { user } = useAuth();
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [fields, setFields] = useState([]);
  const [presets, setPresets] = useState({});
  const [limits, setLimits] = useState({});
  const [supportedFormats, setSupportedFormats] = useState(['csv', 'xlsx', 'pdf']);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [scope, setScope] = useState('filtered');
  const [format, setFormat] = useState('pdf');
  const [submitting, setSubmitting] = useState(false);
  const [syncSubmitting, setSyncSubmitting] = useState(false);

  const trimmedSearch = (search ?? '').trim();
  const maxRowsSync = Number(limits.max_rows_sync) || 500;
  const maxRowsAsync = Number(limits.max_rows_async) || 50000;

  const formatOptions = useMemo(
    () =>
      FACILITY_EXPORT_FORMATS.filter((f) => supportedFormats.includes(f.value)).map((f) => ({
        label: (
          <Space size={4}>
            {FORMAT_ICONS[f.value]}
            <span>{f.label}</span>
          </Space>
        ),
        value: f.value,
      })),
    [supportedFormats],
  );

  const selectedFormatMeta = FACILITY_EXPORT_FORMATS.find((f) => f.value === format);

  const canInstantDownload = useMemo(() => {
    if (shouldUseClientInstantExport(format)) return true;
    if (scope !== 'filtered') return estimatedRows == null || Number(estimatedRows) <= maxRowsSync;
    if (estimatedRows == null || Number.isNaN(Number(estimatedRows))) return true;
    return Number(estimatedRows) <= maxRowsSync;
  }, [format, scope, estimatedRows, maxRowsSync]);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const resp = await lib.getFacilityExportFields();
      const parsed = parseFacilityExportFieldsResponse(resp);
      setFields(parsed.fields);
      setPresets(parsed.presets);
      setLimits(parsed.limits);
      setSelectedKeys(defaultFacilityExportFieldKeys(parsed.fields));
      setSupportedFormats(parsed.supportedFormats);
      setFormat((current) =>
        parsed.supportedFormats.includes(current) ? current : parsed.supportedFormats[0] || 'csv',
      );
    } catch (e) {
      setCatalogError(e?.message || 'Failed to load export fields');
      setFields([]);
      setSelectedKeys([]);
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setScope(trimmedSearch ? 'filtered' : 'all');
    loadCatalog();
  }, [open, loadCatalog]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldsByGroup = useMemo(() => {
    const groups = {};
    fields.forEach((f) => {
      const g = f.group || 'other';
      if (!groups[g]) groups[g] = [];
      groups[g].push(f);
    });
    return groups;
  }, [fields]);

  const applyPreset = (presetKey) => {
    const keys = presets[presetKey];
    if (!Array.isArray(keys) || !keys.length) return;
    const allowed = new Set(fields.map((f) => f.key));
    setSelectedKeys(keys.filter((k) => allowed.has(k)));
  };

  const toggleField = (key, checked) => {
    setSelectedKeys((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((k) => k !== key);
    });
  };

  const buildParams = () => ({
    scope,
    search: scope === 'filtered' ? trimmedSearch : undefined,
    fields: sanitizeFacilityExportFieldKeys(selectedKeys),
    format,
  });

  const exportMeta = useMemo(
    () => ({
      exportedBy:
        user?.profile?.user?.email ||
        user?.profile?.email ||
        user?.email ||
        user?.username ||
        undefined,
    }),
    [user],
  );

  const handleQueue = async () => {
    if (!selectedKeys.length) {
      notification.warning({ message: 'Select at least one column to export.' });
      return;
    }
    setSubmitting(true);
    try {
      if (!isServerQueuedExportFormat(format)) {
        const result = await downloadFacilityExportClient({
          ...buildParams(),
          catalogFields: fields,
          meta: exportMeta,
          maxRows: maxRowsAsync,
        });
        if (result?.useAsync) {
          notification.warning({
            message: 'Export too large',
            description: `This dataset exceeds the instant ${FORMAT_LABELS[format]} limit. Narrow your search or queue a CSV export instead.`,
          });
          return;
        }
        notifyFacilityDownloadComplete(format, result?.filename);
        onClose();
        return;
      }

      await queueFacilityExportJob(buildParams());
      notifyFacilityExportQueued(format, onViewExports);
      onClose();
    } catch (e) {
      notifyFacilityExportFailed(e);
    } finally {
      setSubmitting(false);
    }
  };

  const runClientExport = async () => {
    const result = await downloadFacilityExportClient({
      ...buildParams(),
      catalogFields: fields,
      meta: exportMeta,
      maxRows: maxRowsAsync,
    });
    if (result?.useAsync) {
      if (isServerQueuedExportFormat(format)) {
        notification.info({
          message: 'Large export',
          description: 'Too many rows for instant download. Queuing CSV export instead.',
        });
        await queueFacilityExportJob({ ...buildParams(), format: 'csv' });
        notifyFacilityExportQueued('csv', onViewExports);
        onClose();
        return;
      }
      notification.warning({
        message: 'Export too large',
        description: `Too many rows for instant ${FORMAT_LABELS[format]}. Narrow your search or queue a CSV export.`,
      });
      return;
    }
    notifyFacilityDownloadComplete(format, result?.filename);
    onClose();
  };

  const handleInstantDownload = async () => {
    if (!selectedKeys.length) {
      notification.warning({ message: 'Select at least one column to export.' });
      return;
    }
    setSyncSubmitting(true);
    try {
      if (shouldUseClientInstantExport(format)) {
        await runClientExport();
        return;
      }

      const result = await trySyncFacilityExport(buildParams());
      if (result?.useAsync) {
        notification.info({
          message: 'Large export',
          description: 'Too many rows for instant download. Queuing CSV export instead.',
        });
        await queueFacilityExportJob({ ...buildParams(), format: 'csv' });
        notifyFacilityExportQueued('csv', onViewExports);
        onClose();
        return;
      }
      notifyFacilityDownloadComplete('csv', result?.filename);
      onClose();
    } catch (e) {
      notifyFacilityExportFailed(e);
    } finally {
      setSyncSubmitting(false);
    }
  };

  const scopeDescription =
    scope === 'filtered'
      ? trimmedSearch
        ? `All facilities matching "${trimmedSearch}" (same filters as directory search).`
        : 'All facilities (no search term — same as an unfiltered directory list).'
      : 'Every facility in the directory. State and status filters apply if set.';

  const instantLabel = FORMAT_LABELS[format] || 'File';

  return (
    <Modal
      title={
        <Space>
          <ExportOutlined />
          <span>Export facilities</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={580}
      destroyOnClose
      zIndex={modalZIndex}
      footer={
        <div className="facilities-export-modal__footer">
          <div className="facilities-export-modal__footer-link">
            {onViewExports ? (
              <Button type="link" onClick={onViewExports}>
                View my exports
              </Button>
            ) : (
              <span />
            )}
          </div>
          <Space size={8} className="facilities-export-modal__footer-actions">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type={isServerQueuedExportFormat(format) ? 'default' : 'primary'}
              icon={<DownloadOutlined />}
              loading={syncSubmitting || (submitting && !isServerQueuedExportFormat(format))}
              disabled={syncSubmitting || submitting || loadingCatalog || !selectedKeys.length}
              onClick={handleInstantDownload}
            >
              Download {instantLabel}
            </Button>
            {isServerQueuedExportFormat(format) ? (
              <Button
                type="primary"
                icon={<ExportOutlined />}
                loading={submitting}
                disabled={syncSubmitting || loadingCatalog || !selectedKeys.length}
                onClick={handleQueue}
              >
                Queue export
              </Button>
            ) : null}
          </Space>
        </div>
      }
    >
      {loadingCatalog ? (
        <div className="facilities-export-modal__loading">
          <Spin tip="Loading export options…">
            <div className="facilities-export-modal__spin-shell" aria-hidden />
          </Spin>
        </div>
      ) : catalogError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load export fields"
          description={catalogError}
          action={
            <Button size="small" onClick={loadCatalog}>
              Retry
            </Button>
          }
        />
      ) : (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Export the full matching directory — not just the current page. PDF includes branded
            letterhead; Excel includes a cover sheet.
          </Paragraph>

          <Text strong>Format</Text>
          <Segmented
            block
            value={format}
            onChange={setFormat}
            options={formatOptions}
            style={{ margin: '8px 0 12px' }}
          />
          {selectedFormatMeta ? (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
              {selectedFormatMeta.description}
            </Text>
          ) : null}

          <Text strong>Scope</Text>
          <Radio.Group
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '8px 0 16px' }}
          >
            <Radio value="filtered">
              Current search
              {trimmedSearch ? (
                <Text type="secondary"> — &quot;{trimmedSearch}&quot;</Text>
              ) : (
                <Text type="secondary"> — all facilities (no search term)</Text>
              )}
            </Radio>
            <Radio value="all">All facilities</Radio>
          </Radio.Group>
          <Alert type="info" showIcon message={scopeDescription} style={{ marginBottom: 16 }} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong>Columns</Text>
            <Space size="small" wrap>
              {Object.keys(presets).map((key) => (
                <Button key={key} size="small" type="link" onClick={() => applyPreset(key)}>
                  {PRESET_LABELS[key] || key}
                </Button>
              ))}
              <Button size="small" type="link" onClick={() => setSelectedKeys(fields.map((f) => f.key))}>
                All
              </Button>
              <Button size="small" type="link" onClick={() => setSelectedKeys([])}>
                Clear
              </Button>
            </Space>
          </div>

          {Object.entries(fieldsByGroup).map(([group, groupFields]) => (
            <div key={group} style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, textTransform: 'capitalize' }}>
                {group}
              </Text>
              <div style={{ marginTop: 4 }}>
                {groupFields.map((f) => (
                  <div key={f.key}>
                    <Checkbox
                      checked={selectedKeys.includes(f.key)}
                      onChange={(e) => toggleField(f.key, e.target.checked)}
                    >
                      {f.label}
                    </Checkbox>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <Divider style={{ margin: '12px 0' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selectedKeys.length} column{selectedKeys.length !== 1 ? 's' : ''} selected
            {maxRowsAsync ? ` · up to ${Number(maxRowsAsync).toLocaleString()} rows` : ''}
            {(format === 'pdf' || format === 'xlsx') ? ' · PDF/Excel download immediately with letterhead' : ''}
            {format === 'csv' && canInstantDownload && estimatedRows != null
              ? ` · server quick download for ≤ ${maxRowsSync} rows`
              : ''}
            {format === 'csv' ? ' · use Queue export for large datasets' : ''}
          </Text>
        </>
      )}
    </Modal>
  );
};

export default FacilitiesExportModal;
